import API from './api';

const STORAGE_VALUE_KEY = 'isafe_pixel_token';
const STORAGE_TS_KEY = 'isafe_pixel_token_ts';
const TOKEN_TTL_MS = 60 * 1000; // 60 seconds

let memoryToken = null;
let memoryTimestamp = 0;
let inFlightPromise = null;
let cachedStorage; // lazily resolved

function getStorage() {
  if (cachedStorage !== undefined) {
    return cachedStorage;
  }

  try {
    if (typeof window === 'undefined') {
      cachedStorage = null;
      return cachedStorage;
    }

    const storage = window.sessionStorage;
    const testKey = '__pixel_token_test__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    cachedStorage = storage;
  } catch (err) {
    // sessionStorage might be unavailable (privacy mode, server render, etc.)
    cachedStorage = null;
  }

  return cachedStorage;
}

function isFresh(timestamp) {
  if (!timestamp) return false;
  return Date.now() - Number(timestamp) < TOKEN_TTL_MS;
}

function storeToken(token) {
  const storage = getStorage();
  const now = Date.now();

  if (storage) {
    try {
      storage.setItem(STORAGE_VALUE_KEY, token || '');
      storage.setItem(STORAGE_TS_KEY, String(now));
    } catch (err) {
      console.warn('Failed to persist pixel token to storage', err);
    }
  } else {
    memoryToken = token;
    memoryTimestamp = now;
  }
}

function readStoredToken() {
  const storage = getStorage();

  if (storage) {
    try {
      const token = storage.getItem(STORAGE_VALUE_KEY);
      const ts = storage.getItem(STORAGE_TS_KEY);
      if (token && isFresh(ts)) {
        return token;
      }
    } catch (err) {
      console.warn('Failed to read pixel token from storage', err);
    }
    return null;
  }

  if (memoryToken && isFresh(memoryTimestamp)) {
    return memoryToken;
  }

  return null;
}

export async function ensurePixelToken() {
  const existing = readStoredToken();
  if (existing) {
    return existing;
  }

  return fetchFreshPixelToken();
}

export async function fetchFreshPixelToken() {
  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = (async () => {
    const token = await API.fetchPixel();
    if (token) {
      storeToken(token);
    }
    return token;
  })();

  try {
    const token = await inFlightPromise;
    return token;
  } finally {
    inFlightPromise = null;
  }
}

export function clearPixelToken() {
  const storage = getStorage();
  if (storage) {
    try {
      storage.removeItem(STORAGE_VALUE_KEY);
      storage.removeItem(STORAGE_TS_KEY);
    } catch (err) {
      console.warn('Failed to clear pixel token storage', err);
    }
  }
  memoryToken = null;
  memoryTimestamp = 0;
}
